import type {
  GeocodingFeature,
  GeocodingResponse,
} from "@mapbox/search-js-core";
import type { SearchHook, SearchHookOptions } from "../../../types";
import { geocodingFeatureToLocation } from "../utils";

/** Geocoding covers countries outside Search Box coverage, including New Zealand. */
export function useGeocodingSearch({
  token,
  types,
  proximity,
}: SearchHookOptions): SearchHook {
  async function request(
    endpoint: "forward" | "reverse",
    params: Record<string, string>,
  ) {
    const url = new URL(`https://api.mapbox.com/search/geocode/v6/${endpoint}`);
    url.search = new URLSearchParams({
      access_token: token,
      // The selected town/postcode may be saved with a seller's CRM inquiry.
      permanent: "true",
      ...(types?.length ? { types: types.join(",") } : {}),
      ...params,
    }).toString();
    const response = await fetch(url);
    if (!response.ok)
      throw new Error(`Location search failed (${response.status})`);
    const result: GeocodingResponse = await response.json();
    return { ...result, url: url.toString() };
  }
  return {
    async suggest(text, { countries, bbox } = {}) {
      const result = await request("forward", {
        q: text,
        autocomplete: "true",
        ...(countries?.length
          ? { country: countries.join(",").toLowerCase() }
          : {}),
        ...(proximity
          ? { proximity: `${proximity.lng},${proximity.lat}` }
          : {}),
        ...(bbox
          ? {
              bbox: `${bbox.sw.lng},${bbox.sw.lat},${bbox.ne.lng},${bbox.ne.lat}`,
            }
          : {}),
      });
      return {
        url: result.url,
        attribution: result.attribution,
        suggestions: result.features.map((feature) => ({
          name: feature.properties.name,
          formattedLocation: feature.properties.place_formatted,
          address: feature.properties.full_address,
          _raw: feature,
        })),
      };
    },
    async retrieve(suggestion) {
      return {
        locations: [
          geocodingFeatureToLocation(suggestion._raw as GeocodingFeature),
        ],
      };
    },
    async fromCoords(coords) {
      const result = await request("reverse", {
        longitude: String(coords.lng),
        latitude: String(coords.lat),
      });
      return {
        ...result,
        locations: result.features.map(geocodingFeatureToLocation),
      };
    },
  };
}
